import { CountryCode, CurrencyCode } from '@dayetopia/library';
import { BGN_TO_EUR_RATE } from '@contracts';

// EUR to BGN rate (inverse of BGN_TO_EUR_RATE)
export const EUR_TO_BGN_RATE = 1 / BGN_TO_EUR_RATE;

/**
 * Countries that display dual currency (EUR primary, BGN secondary)
 * Bulgaria shows EUR as primary with BGN equivalent for reference
 */
export const DUAL_CURRENCY_COUNTRIES: CountryCode[] = [CountryCode.BG];

/**
 * Converts BGN amount to EUR using the static conversion rate
 * @param bgnAmount - Amount in Bulgarian Lev
 * @returns Amount in EUR rounded to 2 decimal places, or 0 for invalid inputs
 */
export const convertBGNToEUR = (bgnAmount: number, notFixed: boolean = false): number => {
  if (!Number.isFinite(bgnAmount)) {
    return 0;
  }
  return notFixed ? bgnAmount * BGN_TO_EUR_RATE : Number((bgnAmount * BGN_TO_EUR_RATE).toFixed(2));
};

/**
 * Converts EUR amount to BGN using the static conversion rate
 * @param eurAmount - Amount in Euros
 * @returns Amount in BGN rounded to 2 decimal places, or 0 for invalid inputs
 */
export const convertEURToBGN = (eurAmount: number, notFixed: boolean = false): number => {
  if (!Number.isFinite(eurAmount)) {
    return 0;
  }
  return notFixed ? eurAmount * EUR_TO_BGN_RATE : Number((eurAmount * EUR_TO_BGN_RATE).toFixed(2));
};

/**
 * Checks if dual currency display should be shown for a given country
 * @param country - Country code
 * @returns True if dual currency should be displayed
 */
export const shouldShowDualCurrency = (country: CountryCode): boolean => {
  return DUAL_CURRENCY_COUNTRIES.includes(country);
};

/**
 * Converts subscription amount from BGN to EUR for display purposes.
 * Legacy Bulgarian subscriptions in Recurly are stored in BGN but should display as EUR.
 * @param amount - Amount from subscription (may be null/undefined)
 * @param subscriptionCurrency - Currency code from the subscription
 * @returns Converted EUR amount if subscription is BGN, otherwise original amount
 */
export const convertSubscriptionAmountForDisplay = (amount: number | null | undefined, subscriptionCurrency: string | undefined): number => {
  if (!amount) return 0;

  const isBGN = subscriptionCurrency === CurrencyCode.BGN;

  return isBGN ? convertBGNToEUR(amount, true) : amount;
};
